class AvatarCacheManager {
  constructor() {
    this.dbName = "AvatarCacheDB";
    this.dbVersion = 1;
    this.storeName = "avatars";
    this.db = null;
  }

  // IndexedDB 초기화
  async initDB() {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.dbVersion);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains(this.storeName)) {
          const store = db.createObjectStore(this.storeName, {
            keyPath: "uid",
          });
          store.createIndex("time", "time", { unique: false });
        }
      };
    });
  }

  // 통합 캐시 조회 (localStorage + IndexedDB를 모두 체크)
  async getSmartAvatarCache(uid, requestedTime = null) {
    try {
      // 1. localStorage에서 먼저 확인 (하위 호환성)
      const localStorageKey = `avatar_${uid}`;
      const localCachedData = localStorage.getItem(localStorageKey);

      if (localCachedData) {
        try {
          const parsedData = JSON.parse(localCachedData);
          const cachedImage = parsedData?.image;
          const cachedTime = parsedData?.time;

          // 캐시 사용 조건 확인
          if (this.shouldUseCache(cachedImage, cachedTime, requestedTime)) {

            // 백그라운드에서 IndexedDB로 마이그레이션 (에러 무시)
            this.backgroundMigration(uid, parsedData);

            return {
              image: cachedImage,
              time: cachedTime,
              source: "localStorage",
            };
          }
        } catch (parseError) {
          // console.warn(`localStorage 데이터 파싱 실패 (${uid}):`, parseError);
          localStorage.removeItem(localStorageKey);
        }
      }

      // 2. IndexedDB에서 확인
      if (this.isIndexedDBSupported()) {
        const cachedData = await this.getRawAvatarFromIndexedDB(uid);
        const cachedImage = cachedData?.image;
        const cachedTime = cachedData?.time;

        if (this.shouldUseCache(cachedImage, cachedTime, requestedTime)) {
          return {
            image: cachedImage,
            time: cachedTime,
            source: "IndexedDB",
          };
        }
      }

      // 3. 캐시 미스
      return null;
    } catch (error) {
      console.error(`캐시 조회 실패 (${uid}):`, error);
      return null;
    }
  }

  // 캐시 사용 조건 판단 로직
  shouldUseCache(cachedImage, cachedTime, requestedTime) {
    if (!cachedImage) {
      return false;
    }

    // requestedTime이 null이면 캐시된 이미지가 있으면 사용
    if (requestedTime === null) {
      return true;
    }

    // requestedTime이 있으면 정확히 일치해야 함
    return cachedTime === requestedTime;
  }

  // 백그라운드 마이그레이션 (에러 발생해도 무시)
  async backgroundMigration(uid, localData) {
    setTimeout(async () => {
      try {
        const response = await fetch(localData.image);
        const blob = await response.blob();
        await this.saveAvatarToCache(uid, blob, localData.time);
        localStorage.removeItem(`avatar_${uid}`);
      } catch (error) {
        // 에러 무시 (백그라운드 작업이므로)
      }
    }, 100);
  }

  // 캐시에서 아바타 데이터 조회 (기존 인터페이스 유지하면서 스마트 캐시 사용)
  async getAvatarFromCache(uid) {
    try {
      // 내부적으로 스마트 캐시 조회 사용 (기존 코드 호환성 유지)
      const smartResult = await this.getSmartAvatarCache(uid, null);
      return smartResult; // 기존 형식과 동일
    } catch (error) {
      console.error("캐시에서 아바타 조회 실패:", error);
      return null;
    }
  }

  // 원본 IndexedDB 조회 (내부용)
  async getRawAvatarFromIndexedDB(uid) {
    try {
      await this.initDB();

      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        const request = store.get(uid);

        request.onsuccess = () => {
          const result = request.result;
          resolve(result || null);
        };

        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("IndexedDB에서 아바타 조회 실패:", error);
      return null;
    }
  }

  // 캐시에 아바타 데이터 저장 (로그 개선)
  async saveAvatarToCache(uid, imageBlob, time) {
    try {
      await this.initDB();

      // 트랜잭션 생성 전에 base64 변환 완료 (중요!)
      const base64Image = await this.blobToBase64(imageBlob);

      const avatarData = {
        uid: uid,
        image: base64Image,
        time: time,
        timestamp: Date.now(),
      };

      // base64 변환 완료 후 트랜잭션 시작
      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        // 트랜잭션 에러 핸들링 추가
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(new Error("Transaction aborted"));

        const request = store.put(avatarData);
        request.onsuccess = () => {
          resolve(avatarData);
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("캐시 저장 실패:", error);
      throw error;
    }
  }

  // 특정 아바타 캐시 삭제
  async removeAvatarFromCache(uid) {
    try {
      await this.initDB();

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(new Error("Transaction aborted"));

        const request = store.delete(uid);
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("캐시 삭제 실패:", error);
      throw error;
    }
  }

  // 전체 캐시 삭제
  async clearAllCache() {
    try {
      await this.initDB();

      const transaction = this.db.transaction([this.storeName], "readwrite");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(new Error("Transaction aborted"));

        const request = store.clear();
        request.onsuccess = () => {
          resolve();
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("전체 캐시 삭제 실패:", error);
      throw error;
    }
  }

  // Blob을 Base64로 변환
  blobToBase64(blob) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // localStorage에서 IndexedDB로 데이터 마이그레이션 (개선된 버전)
  async migrateFromLocalStorage() {
    try {
      const keys = Object.keys(localStorage).filter((key) =>
        key.startsWith("avatar_")
      );

      if (keys.length === 0) {
        return;
      }


      let successCount = 0;
      let skipCount = 0;

      // 순차 처리
      for (const key of keys) {
        try {
          const uid = key.replace("avatar_", "");
          const cachedData = JSON.parse(localStorage.getItem(key));

          if (cachedData && cachedData.image) {
            // 이미 IndexedDB에 있는지 확인
            const existingData = await this.getRawAvatarFromIndexedDB(uid);
            if (!existingData) {
              // base64를 blob으로 변환하여 IndexedDB에 저장
              const response = await fetch(cachedData.image);
              const blob = await response.blob();
              await this.saveAvatarToCache(uid, blob, cachedData.time);
              successCount++;
            } else {
              skipCount++;
            }

            // localStorage에서 삭제
            localStorage.removeItem(key);
          }
        } catch (itemError) {
          // console.warn(`마이그레이션 아이템 처리 실패 (${key}):`, itemError);
          continue;
        }
      }

      //   `✅ 마이그레이션 완료 - 성공: ${successCount}, 건너뜀: ${skipCount}`
      // );
    } catch (error) {
      console.error("마이그레이션 실패:", error);
    }
  }

  // 캐시 정보 조회 (개발/디버깅용)
  async getCacheInfo() {
    try {
      await this.initDB();

      const transaction = this.db.transaction([this.storeName], "readonly");
      const store = transaction.objectStore(this.storeName);

      return new Promise((resolve, reject) => {
        transaction.onerror = () => reject(transaction.error);

        const request = store.getAll();
        request.onsuccess = () => {
          const allData = request.result;
          let totalSize = 0;

          allData.forEach((item) => {
            if (item.image) {
              totalSize += item.image.length;
            }
          });

          resolve({
            count: allData.length,
            totalSize: this.formatBytes(totalSize),
            items: allData.map((item) => ({
              uid: item.uid,
              time: item.time,
              size: this.formatBytes(item.image ? item.image.length : 0),
              timestamp: new Date(item.timestamp).toLocaleString(),
            })),
          });
        };
        request.onerror = () => reject(request.error);
      });
    } catch (error) {
      console.error("캐시 정보 조회 실패:", error);
      return {
        count: 0,
        totalSize: "0 Bytes",
        items: [],
      };
    }
  }

  // 바이트 크기 포맷팅
  formatBytes(bytes, decimals = 2) {
    if (bytes === 0) return "0 Bytes";

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB"];

    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  }

  // IndexedDB 지원 여부 확인
  isIndexedDBSupported() {
    return typeof indexedDB !== "undefined";
  }

  // 안전한 저장 (fallback 포함)
  async safeStore(uid, imageBlob, time) {
    if (!this.isIndexedDBSupported()) {
      // console.warn("IndexedDB 미지원, localStorage 사용");
      // localStorage fallback
      const base64Image = await this.blobToBase64(imageBlob);
      localStorage.setItem(
        `avatar_${uid}`,
        JSON.stringify({
          image: base64Image,
          time: time,
        })
      );
      return { image: base64Image, time: time };
    }

    try {
      return await this.saveAvatarToCache(uid, imageBlob, time);
    } catch (error) {
      // console.warn("IndexedDB 저장 실패, localStorage 사용:", error);
      // localStorage fallback
      const base64Image = await this.blobToBase64(imageBlob);
      localStorage.setItem(
        `avatar_${uid}`,
        JSON.stringify({
          image: base64Image,
          time: time,
        })
      );
      return { image: base64Image, time: time };
    }
  }
}

// 싱글톤 인스턴스 생성
const avatarCacheManager = new AvatarCacheManager();

export default avatarCacheManager;
